import { useState } from 'react';
import { TrendingUp, Users, Activity, AlertTriangle, Calendar, BarChart3 } from 'lucide-react';
import { ComingSoonWatermark } from '@/components/ComingSoonWatermark';

/**
 * BusinessForecastPage - Capacity forecasting and workload planning
 * 
 * This page provides:
 * - Process capacity forecasting per employee
 * - System load and capacity limits
 * - Resource allocation predictions
 * - Bottleneck identification
 * - Demand vs capacity analysis
 */

interface ProcessCapacity {
  processName: string;
  currentLoad: number;
  maxCapacity: number;
  assignedEmployees: number;
  avgProcessingTime: number; // minutes
  unit: string;
  forecastedLoad: number;
  status: 'healthy' | 'warning' | 'critical';
}

interface SystemCapacity {
  systemName: string;
  currentLoad: number;
  maxCapacity: number;
  unit: string;
  utilizationRate: number;
  peakUsage: number;
  avgResponseTime: number;
  status: 'healthy' | 'warning' | 'critical';
}

const mockProcessCapacity: ProcessCapacity[] = [
  {
    processName: 'Risk Assessments',
    currentLoad: 42,
    maxCapacity: 65,
    assignedEmployees: 8,
    avgProcessingTime: 180,
    unit: 'assessments/month',
    forecastedLoad: 58,
    status: 'warning',
  },
  {
    processName: 'Compliance Reviews',
    currentLoad: 28,
    maxCapacity: 40,
    assignedEmployees: 5,
    avgProcessingTime: 240,
    unit: 'reviews/month',
    forecastedLoad: 35,
    status: 'warning',
  },
  {
    processName: 'Incident Reports',
    currentLoad: 156,
    maxCapacity: 250,
    assignedEmployees: 12,
    avgProcessingTime: 45,
    unit: 'reports/month',
    forecastedLoad: 189,
    status: 'healthy',
  },
  {
    processName: 'Policy Reviews',
    currentLoad: 12,
    maxCapacity: 15,
    assignedEmployees: 3,
    avgProcessingTime: 360,
    unit: 'policies/quarter',
    forecastedLoad: 14,
    status: 'critical',
  },
  {
    processName: 'Supplier Due Diligence',
    currentLoad: 24,
    maxCapacity: 30,
    assignedEmployees: 6,
    avgProcessingTime: 480,
    unit: 'reviews/quarter',
    forecastedLoad: 28,
    status: 'warning',
  },
];

const mockSystemCapacity: SystemCapacity[] = [
  {
    systemName: 'Risk Management Platform',
    currentLoad: 2847,
    maxCapacity: 5000,
    unit: 'concurrent users',
    utilizationRate: 57,
    peakUsage: 3200,
    avgResponseTime: 245,
    status: 'healthy',
  },
  {
    systemName: 'Document Management',
    currentLoad: 4200,
    maxCapacity: 5000,
    unit: 'documents/day',
    utilizationRate: 84,
    peakUsage: 4850,
    avgResponseTime: 1200,
    status: 'warning',
  },
  {
    systemName: 'Reporting Engine',
    currentLoad: 1850,
    maxCapacity: 2000,
    unit: 'reports/day',
    utilizationRate: 93,
    peakUsage: 1980,
    avgResponseTime: 3400,
    status: 'critical',
  },
  {
    systemName: 'Integration API',
    currentLoad: 125000,
    maxCapacity: 250000,
    unit: 'requests/hour',
    utilizationRate: 50,
    peakUsage: 180000,
    avgResponseTime: 85,
    status: 'healthy',
  },
];

const getStatusColor = (status: string) => {
  switch (status) {
    case 'healthy':
      return 'text-success-500 bg-success-500/20 border-success-500/30';
    case 'warning':
      return 'text-warning-500 bg-warning-500/20 border-warning-500/30';
    case 'critical':
      return 'text-danger-500 bg-danger-500/20 border-danger-500/30';
    default:
      return 'text-neutral-500 bg-neutral-500/20 border-neutral-500/30';
  }
};

export function BusinessForecastPage() {
  const [processCapacity] = useState<ProcessCapacity[]>(mockProcessCapacity);
  const [systemCapacity] = useState<SystemCapacity[]>(mockSystemCapacity);

  const healthyProcesses = processCapacity.filter(p => p.status === 'healthy').length;
  const criticalProcesses = processCapacity.filter(p => p.status === 'critical').length;
  const avgUtilization = Math.round(
    systemCapacity.reduce((sum, s) => sum + s.utilizationRate, 0) / systemCapacity.length
  );
  const overloadedSystems = systemCapacity.filter(s => s.utilizationRate >= 85).length;

  return (
    <div className="space-y-6">
      <ComingSoonWatermark
        pageKey="business-forecast"
        icon={TrendingUp}
        title="Business Forecast"
        description="Capacity forecasting and workload planning for processes and systems"
      />
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold gradient-text">Business Forecast</h1>
          <p className="text-neutral-400 mt-2">
            Capacity forecasting, workload planning, and resource allocation across processes and systems
          </p>
        </div>
        <button className="btn-primary">
          <Calendar className="w-4 h-4 mr-2" />
          Generate Forecast
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="card">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-success-500/20 rounded-lg">
              <Activity className="w-5 h-5 text-success-500" />
            </div>
            <div>
              <p className="text-sm text-neutral-400">Healthy Processes</p>
              <p className="text-2xl font-bold text-white">{healthyProcesses}</p>
            </div>
          </div>
          <p className="text-xs text-neutral-500">Within capacity limits</p>
        </div>

        <div className="card">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-danger-500/20 rounded-lg">
              <AlertTriangle className="w-5 h-5 text-danger-500" />
            </div>
            <div>
              <p className="text-sm text-neutral-400">Critical Processes</p>
              <p className="text-2xl font-bold text-white">{criticalProcesses}</p>
            </div>
          </div>
          <p className="text-xs text-neutral-500">Requires immediate action</p>
        </div>

        <div className="card">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-primary-500/20 rounded-lg">
              <BarChart3 className="w-5 h-5 text-primary-500" />
            </div>
            <div>
              <p className="text-sm text-neutral-400">Avg System Load</p>
              <p className="text-2xl font-bold text-white">{avgUtilization}%</p>
            </div>
          </div>
          <p className="text-xs text-neutral-500">Across all systems</p>
        </div>

        <div className="card">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-warning-500/20 rounded-lg">
              <TrendingUp className="w-5 h-5 text-warning-500" />
            </div>
            <div>
              <p className="text-sm text-neutral-400">Overloaded Systems</p>
              <p className="text-2xl font-bold text-white">{overloadedSystems}</p>
            </div>
          </div>
          <p className="text-xs text-neutral-500">≥85% utilization</p>
        </div>
      </div>

      {/* Process Capacity Forecast */}
      <div className="card">
        <div className="card-header">
          <h2 className="text-lg font-semibold">Process Capacity Forecast</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-neutral-800">
                <th className="text-left p-4 text-sm font-medium text-neutral-400">Process</th>
                <th className="text-left p-4 text-sm font-medium text-neutral-400">Current Load</th>
                <th className="text-left p-4 text-sm font-medium text-neutral-400">Max Capacity</th>
                <th className="text-left p-4 text-sm font-medium text-neutral-400">Assigned Staff</th>
                <th className="text-left p-4 text-sm font-medium text-neutral-400">Avg Time</th>
                <th className="text-left p-4 text-sm font-medium text-neutral-400">Forecasted</th>
                <th className="text-left p-4 text-sm font-medium text-neutral-400">Status</th>
              </tr>
            </thead>
            <tbody>
              {processCapacity.map((process, index) => (
                <tr key={index} className="border-b border-neutral-800 hover:bg-neutral-800/30 transition-colors">
                  <td className="p-4">
                    <span className="text-white font-medium">{process.processName}</span>
                  </td>
                  <td className="p-4">
                    <div className="space-y-1">
                      <span className="text-white font-medium">{process.currentLoad}</span>
                      <span className="text-xs text-neutral-400 block">{process.unit}</span>
                    </div>
                  </td>
                  <td className="p-4">
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <div className="flex-1 h-2 bg-neutral-700 rounded-full overflow-hidden max-w-[100px]">
                          <div
                            className={`h-full ${
                              process.status === 'critical' ? 'bg-danger-500' :
                              process.status === 'warning' ? 'bg-warning-500' :
                              'bg-success-500'
                            }`}
                            style={{ width: `${(process.currentLoad / process.maxCapacity) * 100}%` }}
                          ></div>
                        </div>
                        <span className="text-sm text-neutral-400">{process.maxCapacity}</span>
                      </div>
                      <span className="text-xs text-neutral-500">
                        {Math.round((process.currentLoad / process.maxCapacity) * 100)}% utilized
                      </span>
                    </div>
                  </td>
                  <td className="p-4">
                    <div className="flex items-center gap-2">
                      <Users className="w-4 h-4 text-primary-400" />
                      <span className="text-white">{process.assignedEmployees}</span>
                    </div>
                  </td>
                  <td className="p-4">
                    <span className="text-neutral-300">{process.avgProcessingTime}m</span>
                  </td>
                  <td className="p-4">
                    <div className="space-y-1">
                      <span className="text-white font-medium">{process.forecastedLoad}</span>
                      <div className="flex items-center gap-1">
                        <TrendingUp className="w-3 h-3 text-warning-500" />
                        <span className="text-xs text-warning-500">
                          +{Math.round(((process.forecastedLoad - process.currentLoad) / process.currentLoad) * 100)}%
                        </span>
                      </div>
                    </div>
                  </td>
                  <td className="p-4">
                    <span className={`badge ${getStatusColor(process.status)}`}>
                      {process.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* System Capacity */}
      <div className="card">
        <div className="card-header">
          <h2 className="text-lg font-semibold">System Capacity & Performance</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-neutral-800">
                <th className="text-left p-4 text-sm font-medium text-neutral-400">System</th>
                <th className="text-left p-4 text-sm font-medium text-neutral-400">Current Load</th>
                <th className="text-left p-4 text-sm font-medium text-neutral-400">Max Capacity</th>
                <th className="text-left p-4 text-sm font-medium text-neutral-400">Utilization</th>
                <th className="text-left p-4 text-sm font-medium text-neutral-400">Peak Usage</th>
                <th className="text-left p-4 text-sm font-medium text-neutral-400">Response Time</th>
                <th className="text-left p-4 text-sm font-medium text-neutral-400">Status</th>
              </tr>
            </thead>
            <tbody>
              {systemCapacity.map((system, index) => (
                <tr key={index} className="border-b border-neutral-800 hover:bg-neutral-800/30 transition-colors">
                  <td className="p-4">
                    <span className="text-white font-medium">{system.systemName}</span>
                  </td>
                  <td className="p-4">
                    <div className="space-y-1">
                      <span className="text-white font-medium">{system.currentLoad.toLocaleString()}</span>
                      <span className="text-xs text-neutral-400 block">{system.unit}</span>
                    </div>
                  </td>
                  <td className="p-4">
                    <span className="text-neutral-300">{system.maxCapacity.toLocaleString()}</span>
                  </td>
                  <td className="p-4">
                    <div className="space-y-2">
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-neutral-400">Load</span>
                        <span className={`font-medium ${
                          system.utilizationRate >= 85 ? 'text-danger-500' :
                          system.utilizationRate >= 70 ? 'text-warning-500' :
                          'text-success-500'
                        }`}>
                          {system.utilizationRate}%
                        </span>
                      </div>
                      <div className="h-2 bg-neutral-700 rounded-full overflow-hidden">
                        <div
                          className={`h-full ${
                            system.utilizationRate >= 85 ? 'bg-danger-500' :
                            system.utilizationRate >= 70 ? 'bg-warning-500' :
                            'bg-success-500'
                          }`}
                          style={{ width: `${system.utilizationRate}%` }}
                        ></div>
                      </div>
                    </div>
                  </td>
                  <td className="p-4">
                    <span className="text-neutral-300">{system.peakUsage.toLocaleString()}</span>
                  </td>
                  <td className="p-4">
                    <span className="text-neutral-300">{system.avgResponseTime}ms</span>
                  </td>
                  <td className="p-4">
                    <span className={`badge ${getStatusColor(system.status)}`}>
                      {system.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
